import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { getDynamicData } from "~/common/Store/actions/updateUserAndChatFromOrigin";
import { getCarouselInteraction, Item } from "~/userAnalytics";
import {
  carouselIdDimension,
  itemNumberDimension,
  productReviewCountDimension,
  vendorDimension,
} from "./definitions";
import { CustomerIoProduct } from "~/analytics/CustomerIo/CustomerIoEvents";

type MarketingProductResult = {
  description: string;
  feedIdentifier: string;
  price: number;
  brand: string;
  category: string;
  itemNumber: string;
  vendorCode: string;
  productReviewCount: number;
  categoryId: number;
  lowestBreadcrumb?: string;
  secondLowestBreadcrumb?: string;
  imageUrl?: string;
  overallRating?: number;
};

export interface AnalyticsProduct {
  name: string;
  id: string;
  price: string;
  brand: string;
  category: string;
  itemNumber?: string;
  quantity?: string;
  dimension39?: string;
  dimension40?: string;
  dimension52?: string;
  dimension64?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_name?: string;
  position?: number;
  categoryId: number;
  feedIdentifier?: string;
  lowestBreadcrumb?: string;
  secondLowestBreadcrumb?: string;
  imageUrl?: string;
  overallRating?: number;
}

export const getProductData = async (
  itemNumbers: string,
): Promise<Throwable<MarketingProductResult[]>> => {
  const result = await getDynamicData;
  if (result.isError) return result;
  const formattedItemNumbers = encodeURIComponent(itemNumbers);
  const getProductDataUrl = `/marketing:analytics/getproductdataforanalytics/?itemNumbers=${formattedItemNumbers}`;

  try {
    const response = await fetch(getProductDataUrl, { method: "GET" });
    if (!response.ok) {
      return {
        isError: true,
        error: new Error(
          "Failed to fetch product data from marketing analytics",
          { cause: response.statusText },
        ),
      };
    }
    const products: MarketingProductResult[] = await response.json();
    return { isError: false, value: products };
  } catch (e) {
    return { isError: true, error: toError(e) };
  }
};

export const createProducts = async (
  items: Item[],
  includeBreadcrumbs = false,
) => {
  const products: AnalyticsProduct[] = [];
  const itemNumbers = items.map((item: Item) => item.itemNumber);
  if (!itemNumbers || itemNumbers.length === 0) {
    return [];
  }

  const data = await getProductData(itemNumbers.toString());
  if (data.isError) {
    return [];
  }

  data.value.forEach((result: MarketingProductResult, index: number) => {
    const productLookup = items.find(
      (item) =>
        item.itemNumber.trim().toUpperCase() ===
        result.itemNumber.trim().toUpperCase(),
    );

    const carouselLookup = getCarouselInteraction();
    const carouselId =
      carouselLookup[result.itemNumber.trim().toUpperCase()] ?? "";

    const categories = result.category.split("/");

    const product: AnalyticsProduct = {
      name: result.description,
      id: result.feedIdentifier,
      price: result.price.toString(),
      brand: result.brand,
      category: result.category,
      itemNumber: result.itemNumber,
      [vendorDimension]: result.vendorCode,
      [itemNumberDimension]: result.itemNumber.toUpperCase().trim(),
      [carouselIdDimension]: carouselId,
      [productReviewCountDimension]: result.productReviewCount.toString(),
      item_category: categories[0],
      item_category2: categories[1],
      item_category3: categories[2],
      item_category4: categories[3],
      item_category5: categories[4],
      position: index,
      categoryId: result.categoryId,
      feedIdentifier: result.feedIdentifier,
      imageUrl: result.imageUrl,
      overallRating: result.overallRating,
    };

    if (includeBreadcrumbs) {
      product.lowestBreadcrumb = result.lowestBreadcrumb;
      product.secondLowestBreadcrumb = result.secondLowestBreadcrumb;
    }

    if (productLookup?.quantity) {
      product.quantity = productLookup.quantity.toString();
    } else if (productLookup?.qty) {
      product.quantity = productLookup.qty.toString();
    } else {
      product.quantity = "0";
    }

    products.push(product);
  });

  return products;
};

export const createListingProduct = async (productElement: Element) => {
  const itemNumber = productElement.getAttribute("data-itemnumber");
  if (!itemNumber) return [];
  const products = await createProducts([{ itemNumber }]);

  if (products[0]) {
    const returnedProduct = products[0];
    const position = Number(productElement.getAttribute("data-position"));
    if (position && position >= 0) {
      returnedProduct.position = position;
    }
  }
  return products;
};

type OptionalCustomerIoFields =
  | "brand"
  | "quantity"
  | "reviews"
  | "overallRating"
  | "imageUrl";

const customerIoPropertyMap = {
  brand: "item_brand",
  quantity: "item_quantity",
  reviews: "item_review_count",
  overallRating: "item_review_rating",
  imageUrl: "item_image_url",
};

const formatOptionalFields = (
  baseProduct: AnalyticsProduct,
  optionalFields: (keyof typeof customerIoPropertyMap)[],
  customerIoProduct: CustomerIoProduct,
) => {
  const newProduct = { ...customerIoProduct };
  if (optionalFields.includes("brand")) {
    newProduct.item_brand = baseProduct.brand;
  }
  if (optionalFields.includes("quantity")) {
    newProduct.item_quantity = baseProduct.quantity?.toString();
  }
  if (optionalFields.includes("reviews")) {
    newProduct.item_review_count =
      baseProduct[productReviewCountDimension]?.toString();
  }
  if (optionalFields.includes("overallRating")) {
    newProduct.item_review_rating = baseProduct.overallRating;
  }
  if (optionalFields.includes("imageUrl")) {
    newProduct.item_image_url = baseProduct.imageUrl ?? "";
  }
  return newProduct;
};

export const formatProductsForCustomerIo = (
  baseProducts: AnalyticsProduct[],
  optionalFields: OptionalCustomerIoFields[] = [],
): CustomerIoProduct[] => {
  const products: CustomerIoProduct[] = [];

  baseProducts.forEach((baseProduct: AnalyticsProduct) => {
    const customerIoProduct: CustomerIoProduct = {
      item_category1: baseProduct.item_category,
      item_category2: baseProduct.item_category2,
      item_category3: baseProduct.item_category3,
      item_category4: baseProduct.item_category4,
      item_category5: baseProduct.item_category5,
      item_id: baseProduct.itemNumber ?? "",
      item_name: baseProduct.name,
      item_price: baseProduct.price.toString(),
      lowest_breadcrumb: baseProduct.lowestBreadcrumb,
      second_lowest_breadcrumb: baseProduct.secondLowestBreadcrumb,
    };

    const formattedProduct = formatOptionalFields(
      baseProduct,
      optionalFields,
      customerIoProduct,
    );

    products.push(formattedProduct);
  });
  return products;
};

export const formatDefaultProducts = (
  baseProducts: AnalyticsProduct[],
): AnalyticsProduct[] => {
  const products: AnalyticsProduct[] = [];

  baseProducts.forEach((product: AnalyticsProduct) => {
    const { lowestBreadcrumb, secondLowestBreadcrumb, ...rest } = product;
    products.push(rest);
  });
  return products;
};
